import {
  WorkflowRun,
  WorkflowRunStep,
  WorkflowStepConfig,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import {
  quickStudyQuestionCost,
  quickStudyReads,
} from '@/lib/billing/credit-rates'

import i18n from '@/i18n'

import { stimulusRefsFromRun } from '../stimulusRefsFromRun'
import { gateArtifactKey } from './gateArtifacts'

/* Mirrors TWIN_TEST_MAX_PANEL_SIZE in the backend's twin-test constants
   (D15: default 50, maximum 100, floor 10, refused outside those bounds and
   never silently clamped). The fallback here is the ceiling, not the default.

   Used only when no stamped or declared size reached the wire. The backend
   resolves explicit → remembered → default and stamps that number onto the
   run step's `input.config.panelSize`. A gate payload may also carry a
   resolved size. When either is a known (non-default-artifact) stamp, this
   file quotes `~N`. The ceiling is the fallback otherwise. */
const TWIN_TEST_MAX_PANEL_SIZE = 100

/**
 * The last thing a person reads before authorising money.
 *
 * Every number in it is read off the run and its template: the panel size and
 * the fan-out come from the template's step configs, the question count from
 * the artifacts the run actually produced. A hardcoded sentence keeps saying
 * "100 twins" long after the template says 400, and this is the sentence
 * somebody spends against.
 *
 * PEER QUESTIONS ONLY. The billed figure is the peer count (and version reads).
 * Paired follow-ups are no longer part of twin-test spend copy.
 *
 * Only the steps between this gate and the NEXT gate are described: anything
 * past a second approval is not what this click authorises.
 */

/**
 * What one twin-test step will ask for, counted off the artifacts it will ask
 * from: the questions, and the exposures those questions add up to.
 *
 * `cells` is the number the panel size is multiplied by. It is NOT the question
 * count, because a question comparing two or more versions is run once per
 * version, each pass a fresh exposure to its own panel-sized read. Two questions
 * where one compares three versions is four exposures, not two, and the person
 * clicking approve is authorising all four.
 */
interface QuestionSpend {
  questions: number
  cells: number
}

/**
 * Parent/read basis shared by the spend sentence and the QS credit chip.
 * `reads` = cells × panelSize (versions multiply).
 */
export interface RunGateSpendSummary {
  questions: number
  cells: number
  /** The panel this click buys: min(requested panel, matching count). */
  panelSize: number
  /** The panel the step asked for, before any segment shortfall. */
  requestedPanelSize: number
  isKnownPanel: boolean
  reads: number
}

/**
 * What the reviewer knows about the run's segment right now. `matchingCount`
 * is the live count of people the run's population filter matches; absent or
 * null means unknown (or no filter), and the quote stays the panel.
 */
export interface RunGateSegmentQuote {
  matchingCount?: number | null
}

/**
 * The quote rule: a filtered run can only ask the people its filter matches,
 * so the panel this click buys is min(panel, matching). An unknown count
 * never lowers the quote.
 */
export function effectivePanelSize(
  panelSize: number,
  matchingCount: number | null | undefined,
): number {
  if (typeof matchingCount !== 'number' || !Number.isFinite(matchingCount)) {
    return panelSize
  }
  return Math.max(0, Math.min(panelSize, Math.floor(matchingCount)))
}

/**
 * The versions ONE question compares, read on shape alone.
 *
 * Mirrors the backend seam (`twin-test-spec.mapper.ts`): fewer than two entries
 * is not a comparison and costs one exposure. Nothing here reads what a version
 * says, and a malformed one is counted as the single exposure it will run as if
 * the seam lets it through, because this sentence must never over-state a spend.
 */
function countVersions(entry: unknown): number {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return 1
  }
  const versions = (entry as { versions?: unknown }).versions
  if (!Array.isArray(versions) || versions.length < 2) {
    return 1
  }
  return versions.length
}

/**
 * One artifact body as the question writer emits it: JSON, either bare or inside
 * a fenced block. Prose is not a parse failure, it is a question with no
 * versions, so anything that is not JSON simply reads as one exposure.
 *
 * A BARE ARRAY IS A QUESTION LIST. `parseGateQuestions` renders one, so a body
 * this refused to parse rendered N cards and priced as one: the gate showed
 * five questions and quoted the panel once.
 */
function parseArtifactBody(body: string | undefined): unknown {
  const raw = (body ?? '').trim()
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/)
  const candidate = (fenced ? fenced[1] : raw).trim()
  if (!candidate.startsWith('{') && !candidate.startsWith('[')) {
    return null
  }
  try {
    return JSON.parse(candidate) as unknown
  } catch {
    return null
  }
}

/**
 * Bodies as the reviewer has them RIGHT NOW: the gate's staged edits, keyed
 * the way `collectGateArtifacts` keys its cards, win over the artifact on the
 * run. A question removed in the dock is not a question this click spends.
 */
export type GateBodyEdits = Record<string, string>

function stagedArtifactBody(
  stepKey: string,
  artifact: { slug: string; index?: number | null; body: string },
  edits: GateBodyEdits | undefined,
): string {
  if (!edits) {
    return artifact.body
  }
  return (
    edits[gateArtifactKey(stepKey, artifact.slug, artifact.index ?? null)] ??
    artifact.body
  )
}

function countQuestionSpend(
  run: WorkflowRun,
  stepKey: string,
  mediaVersionCount: number,
  edits: GateBodyEdits | undefined,
): QuestionSpend | null {
  const source = run.steps.find((step) => step.stepKey === stepKey)
  const output = source?.outputOverride ?? source?.output
  if (!output || output.kind !== 'artifacts') {
    return null
  }

  let questions = 0
  let cells = 0
  for (const artifact of output.artifacts) {
    const parsed = parseArtifactBody(stagedArtifactBody(stepKey, artifact, edits))
    const payload =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as {
            questions?: unknown
            noMatches?: unknown
            noMatch?: unknown
          })
        : null
    const list = Array.isArray(parsed) ? parsed : payload ? payload.questions : null

    /* BOTH KEYS, THE SHIPPED ONE FIRST. `noMatches` is what the seam emits
       (`buildTwinTestGatePayload`); `noMatch` was the frozen contract's name and
       is still read, because a body written before the seam shipped is still a
       body somebody has to approve. Reading only the retired one prices a
       refusal-only payload as one question through the bare-object fallback
       below, which is a number quoted for work that will never run. This is the
       same both-keys rule `parseGateNoMatches` already follows. */
    const refused = payload
      ? Array.isArray(payload.noMatches)
        ? payload.noMatches
        : payload.noMatch
      : null

    /* A payload that carries ONLY refused questions (C3) spends nothing. It is
       still a well-formed payload, so the bare-object fallback below would
       otherwise price it as one question. */
    if (!Array.isArray(list) && Array.isArray(refused)) {
      continue
    }

    // One artifact carrying a list is the shape a single writer deciding how
    // many questions the subject deserves emits; anything else is one question.
    // The caveat is a SIBLING key and never appears in `questions`, so a
    // refused question is outside this count wherever it travels.
    const entries = Array.isArray(list) ? list : [parsed]
    for (const entry of entries) {
      questions += 1
      cells += Math.max(countVersions(entry), mediaVersionCount)
    }
  }
  return { questions, cells }
}

function asPanelSize(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined
}

function readConfigPanelSize(input: unknown): number | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return undefined
  }
  const record = input as { config?: unknown; panelSize?: unknown }
  const nested =
    record.config &&
    typeof record.config === 'object' &&
    !Array.isArray(record.config)
      ? (record.config as { panelSize?: unknown }).panelSize
      : undefined
  return asPanelSize(nested) ?? asPanelSize(record.panelSize)
}

function readArtifactPanelSize(
  run: WorkflowRun,
  stepKey: string,
  edits: GateBodyEdits | undefined,
): number | undefined {
  const source = run.steps.find((step) => step.stepKey === stepKey)
  const output = source?.outputOverride ?? source?.output
  if (!output || output.kind !== 'artifacts') {
    return undefined
  }

  for (const artifact of output.artifacts) {
    const parsed = parseArtifactBody(stagedArtifactBody(stepKey, artifact, edits))
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      continue
    }
    const panelSize = asPanelSize((parsed as { panelSize?: unknown }).panelSize)
    if (panelSize !== undefined) {
      return panelSize
    }
  }
  return undefined
}

/**
 * The size the backend resolved for this run, if it reached the wire.
 *
 * Run-step config first (resolved at create: explicit → remembered →
 * default), then a gate payload whose caller actually resolved a size.
 * An artifact-only default (100) is the old checker rewrite, not a
 * known size — treat it as absent so the sentence stays "up to".
 */
function readStampedPanelSize(
  run: WorkflowRun,
  step: WorkflowStepConfig,
  edits: GateBodyEdits | undefined,
): number | undefined {
  if (step.kind !== 'quick_study') {
    return undefined
  }
  const row = run.steps.find((entry) => entry.stepKey === step.key)
  const fromConfig = readConfigPanelSize(row?.input)
  if (fromConfig !== undefined) {
    return fromConfig
  }
  const fromArtifact = readArtifactPanelSize(run, step.questionsFrom, edits)
  if (fromArtifact === TWIN_TEST_MAX_PANEL_SIZE) {
    return undefined
  }
  return fromArtifact
}

function summarizeQuickStudyStep(
  step: WorkflowStepConfig,
  run: WorkflowRun,
  template: WorkflowTemplate,
  edits: GateBodyEdits | undefined,
  segment?: RunGateSegmentQuote,
): RunGateSpendSummary | null {
  if (step.kind !== 'quick_study') {
    return null
  }

  const boundMediaCount = stimulusRefsFromRun(run, template).length
  const mediaVersionCount = boundMediaCount >= 2 ? boundMediaCount : 1
  const spend = countQuestionSpend(run, step.questionsFrom, mediaVersionCount, edits)
  const stampedPanelSize = readStampedPanelSize(run, step, edits)
  const requestedPanelSize =
    stampedPanelSize ?? step.panelSize ?? TWIN_TEST_MAX_PANEL_SIZE
  const panelSize = effectivePanelSize(requestedPanelSize, segment?.matchingCount)
  /* A live count below the panel is a number we know, even when the request
     itself was only an "up to" ceiling. */
  const isKnownPanel =
    stampedPanelSize !== undefined ||
    step.panelSize !== undefined ||
    panelSize < requestedPanelSize
  const questions = spend?.questions ?? 0
  const cells = spend?.cells ?? 0

  return {
    questions,
    cells: cells > 0 ? cells : questions,
    panelSize,
    requestedPanelSize,
    isKnownPanel,
    reads: (cells > 0 ? cells : Math.max(questions, 1)) * panelSize,
  }
}

function describeStep(
  step: WorkflowStepConfig,
  run: WorkflowRun,
  template: WorkflowTemplate,
  edits: GateBodyEdits | undefined,
  segment?: RunGateSegmentQuote,
): string | null {
  if (step.kind === 'quick_study') {
    /* QUESTIONS, not versions. The count comes off `questionsFrom`, which is
       the step config's own name for what a quick study asks, so the noun is
       driven by the step kind and holds for every template that ever declares
       one. Peer questions only — no paired-why aside. */
    const summary = summarizeQuickStudyStep(step, run, template, edits, segment)
    if (!summary) {
      return null
    }
    const isShortOfRequest = summary.panelSize < summary.requestedPanelSize
    const panel = isShortOfRequest
      ? i18n.t('threads.detail.spend.knownPanelOfRequested', {
          size: summary.panelSize,
          requested: summary.requestedPanelSize,
          ns: 'app',
        })
      : summary.isKnownPanel
      ? i18n.t('threads.detail.spend.knownPanel', {
          size: summary.panelSize,
          ns: 'app',
        })
      : i18n.t('threads.detail.spend.upToPanel', {
          size: summary.panelSize,
          ns: 'app',
        })
    if (summary.questions === 0) {
      return i18n.t('threads.detail.spend.testsWithPanel', { panel, ns: 'app' })
    }
    const sentence = i18n.t('threads.detail.spend.testsQuestionsWithPanel', {
      count: summary.questions,
      panel,
      ns: 'app',
    })
    if (summary.cells <= summary.questions) {
      return sentence
    }
    /* A question comparing versions is run once per version, so the panel is
       spent that many times over. Counting questions alone would under-state
       this click by exactly the versions it is approving, and the reads are the
       unit the run is charged in. */
    return summary.isKnownPanel
      ? i18n.t('threads.detail.spend.knownReads', {
          sentence,
          reads: summary.reads,
          ns: 'app',
        })
      : i18n.t('threads.detail.spend.upToReads', {
          sentence,
          reads: summary.reads,
          ns: 'app',
        })
  }

  if (step.kind === 'slack_webhook') {
    return i18n.t('threads.detail.spend.postsToSlack', { ns: 'app' })
  }

  return null
}

/**
 * First quick_study step this gate releases, summarized on the same parent/read
 * basis as `buildRunSpendLine`. Null when nothing priced is downstream.
 */
export function summarizeRunGateSpend(
  run: WorkflowRun,
  template: WorkflowTemplate | null,
  gateStep: WorkflowRunStep,
  edits?: GateBodyEdits,
  segment?: RunGateSegmentQuote,
): RunGateSpendSummary | null {
  if (!template) {
    return null
  }

  for (const step of downstreamOf(template, gateStep)) {
    const summary = summarizeQuickStudyStep(step, run, template, edits, segment)
    if (summary) {
      return summary
    }
  }
  return null
}

/**
 * QS credit chip math on the same parent/read basis as the spend sentence.
 * `creativeCells` = version exposures (peers × versions).
 */
export function computeRunGateCreditCost(
  summary: RunGateSpendSummary,
  audienceTier: string,
  quickStudyCreditsPerRead?: number,
): { reads: number; credits: number } {
  const creativeCells = Math.max(summary.cells, 1)
  const twins = summary.panelSize
  const reads = quickStudyReads({ twins, creativeCells })
  const credits = quickStudyQuestionCost(
    audienceTier,
    reads,
    quickStudyCreditsPerRead,
  )
  return { reads, credits }
}

function capitalize(sentence: string): string {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

/**
 * Steps this approval releases: everything after the gate, stopping at the next
 * gate, which is its own decision.
 */
function downstreamOf(
  template: WorkflowTemplate,
  gateStep: WorkflowRunStep,
): Array<WorkflowStepConfig> {
  const gateIndex = template.steps.findIndex(
    (step) => step.key === gateStep.stepKey,
  )
  if (gateIndex < 0) {
    return []
  }

  const downstream: Array<WorkflowStepConfig> = []
  for (const step of template.steps.slice(gateIndex + 1)) {
    if (step.kind === 'gate') {
      break
    }
    downstream.push(step)
  }
  return downstream
}

export function buildRunSpendLine(
  run: WorkflowRun,
  template: WorkflowTemplate | null,
  gateStep: WorkflowRunStep,
  edits?: GateBodyEdits,
  segment?: RunGateSegmentQuote,
): string {
  const remaining = run.steps.length - gateStep.stepIndex - 1
  // No template, no step configs, so the only honest thing left to say is how
  // much of the run is still ahead.
  const fallback =
    remaining > 0
      ? i18n.t('threads.detail.spend.runsRemainingSteps', {
          count: remaining,
          ns: 'app',
        })
      : i18n.t('threads.detail.spend.finishesTheRun', { ns: 'app' })

  if (!template) {
    return fallback
  }

  const clauses = downstreamOf(template, gateStep)
    .map((step) => describeStep(step, run, template, edits, segment))
    .filter((clause): clause is string => clause !== null)

  if (clauses.length === 0) {
    /* This click does not authorise a priced step (a later gate will). An
       empty string keeps the footer on "Look this over, then continue" and
       the button on Continue. Falling through to "Runs the remaining N
       steps" used to make a scan review look like it spent the twin test. */
    return ''
  }

  const [first, ...rest] = clauses
  if (rest.length === 0) {
    return capitalize(first)
  }

  /* The joiner is part of the sentence, not glue: folding each extra clause
     through one locale key keeps punctuation and word order in the hands of
     the translation, instead of welding translated fragments together with an
     English " then ". */
  return rest.reduce(
    (sentence, clause) =>
      i18n.t('threads.detail.spend.thenClause', { sentence, clause, ns: 'app' }),
    capitalize(first),
  )
}
